import dynamic from "next/dynamic";
import { useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useMutation } from "@apollo/client/react/hooks";
import { useMediaQueryContext } from "lib/mediaQueryContext";
import colors from "constants/colors";

import Link from "next/link";
import Radio from "components/shared/radio/Radio";
import Input from "components/shared/input/Input";
import Checkbox from "components/shared/checkbox/Checkbox";
import Button from "components/shared/button/Button";
import IconCheck from "components/shared/svg/IconCheck";
import IconClose from "components/shared/svg/IconClose";
import CardNumberInput from "components/shared/cardNumberInput/CardNumberInput";

import { SetDoublerSubscription } from "../../../helpers/api/cadeauConcepten/mutations/doubler.gql";

import {
    FormStyled,
    Field,
    SectionTitle,
    Label,
    Options,
    StyledLink,
    ModalContainer,
    Close,
    Error,
    Image
} from "./Form.styled";

const Modal = dynamic(() => import("react-modal"));
const Message = dynamic(() => import("components/shared/message/Message"));

export default function Form() {
    const { desktopView, mobileView } = useMediaQueryContext();
    const [submitted, setSubmitted] = useState(false);
    const [modalIsOpen, setIsOpen] = useState(false);

    const methods = useForm({
        mode: "onBlur",
        defaultValues: {
            newsletterOptin: false,
            termsOptin: false
        }
    });
    const { register, watch, errors, handleSubmit, reset } = methods;

    const name = watch("name");
    const email = watch("email");
    const giftcardNumber = watch("code")?.join("");

    const [
        setDoublerSubscription,
        { loading: isLoading, error: hasError }
    ] = useMutation(SetDoublerSubscription, {
        errorPolicy: "all",
        onCompleted: () => {
            reset();
            setSubmitted(true);
        }
    });

    const openModal = () => setIsOpen(true);
    const closeModal = () => setIsOpen(false);

    function onSubmit(data) {
        setDoublerSubscription({
            variables: {
                gender: data.gender,
                name: data.name,
                email: data.email,
                giftcardNumber,
                termsOptin: !!data.termsOptin,
                newsletterOptin: !!data.newsletterOptin
            }
        });

        if (typeof window !== "undefined") {
            window.scrollTo({
                top: 0,
                left: 0,
                behavior: "smooth"
            });
        }
    }

    const disabled = errors && Object.keys(errors).length > 0;
    const codeErrorMessage = errors?.code?.find(
        item => item?.message?.length > 0
    )?.message;

    return (
        <FormStyled>
            {hasError && (
                <Message type="error" title="OEPS! De inschrijving is mislukt.">
                    Het lijkt erop dat er iets fout is gegaan tijdens het
                    inschrijven. Probeer het alsjeblieft opnieuw.
                </Message>
            )}

            {submitted && !hasError ? (
                <Message type="success" title="Deelname bevestigd!">
                    Je ontvangt per e-mail een bevestiging. Mocht je gewonnen
                    hebben, zullen wij je per mail informeren.
                </Message>
            ) : (
                <FormProvider {...methods}>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Field>
                            <SectionTitle>Aanhef</SectionTitle>
                            <Options>
                                <Radio
                                    name="gender"
                                    label="De heer"
                                    value="MALE"
                                    defaultChecked
                                    ref={register}
                                />
                                <Radio
                                    name="gender"
                                    label="Mevrouw"
                                    value="FEMALE"
                                    ref={register}
                                />
                            </Options>
                        </Field>
                        <Field>
                            <Input
                                label="Je volledige naam"
                                type="text"
                                name="name"
                                placeholder="Bijv. Petra van Heemskerk"
                                ref={register({
                                    required: "Naam is hier verplicht",
                                    pattern: /^[A-Za-z ,.'-]+$/
                                })}
                                errorMessage={errors?.name?.message}
                                rightIcon={
                                    name &&
                                    !errors.name && (
                                        <IconCheck fill={colors.green} />
                                    )
                                }
                            />
                        </Field>
                        <Field>
                            <Input
                                label="Je e-mailadres"
                                name="email"
                                type="email"
                                placeholder="Bijv. petra@outlook.com"
                                ref={register({
                                    required: "Email is hier verplicht",
                                    pattern: {
                                        value: /^\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/,
                                        message:
                                            "Voer een geldig e-mailadres in"
                                    }
                                })}
                                errorMessage={errors?.email?.message}
                                rightIcon={
                                    email &&
                                    !errors.email && (
                                        <IconCheck fill={colors.green} />
                                    )
                                }
                            />
                        </Field>
                        <Field>
                            <Label>
                                Vul het 19-cijferig kaartnummer in
                                {desktopView && (
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        onClick={openModal}
                                    >
                                        Waar vind ik mijn kaartnummer?
                                    </Button>
                                )}
                            </Label>
                            <CardNumberInput
                                hasError={codeErrorMessage}
                                errorMessage={codeErrorMessage}
                            />

                            {mobileView && (
                                <Button
                                    type="button"
                                    variant="ghost"
                                    onClick={openModal}
                                >
                                    Waar vind ik mijn kaartnummer?
                                </Button>
                            )}
                            <Modal
                                isOpen={modalIsOpen}
                                contentLabel="Waar vind ik mijn kaartnummer?"
                                onRequestClose={closeModal}
                                ariaHideApp={false}
                                className="react-modal"
                                overlayClassName="react-modal-overlay"
                                closeTimeoutMS={400}
                            >
                                <ModalContainer>
                                    <Image
                                        src="/images/giftcard-backside.svg"
                                        alt="giftcard backside"
                                        loading="lazy"
                                    />
                                    <Close onClick={closeModal}>
                                        <IconClose />
                                    </Close>
                                </ModalContainer>
                            </Modal>
                        </Field>
                        <Field>
                            <Checkbox
                                id="termsOptin"
                                name="termsOptin"
                                type="checkbox"
                                label={
                                    <span htmlFor="termsOptin">
                                        Ik ga akkoord met de algemene{" "}
                                        <Link
                                            href="/algemene-voorwaarden"
                                            passHref
                                        >
                                            <StyledLink>
                                                algemene voorwaarden
                                            </StyledLink>
                                        </Link>{" "}
                                        en ik heb de{" "}
                                        <Link href="/cookie-privacy" passHref>
                                            <StyledLink>
                                                privacyverklaring
                                            </StyledLink>
                                        </Link>{" "}
                                        gelezen.
                                    </span>
                                }
                                ref={register({
                                    required:
                                        "Akkoord gaan met de voorwaarden is hier verplicht"
                                })}
                            />
                            {errors?.termsOptin?.message && (
                                <Error>{errors.termsOptin.message}</Error>
                            )}
                        </Field>

                        <Field>
                            <Checkbox
                                id="newsletterOptin"
                                name="newsletterOptin"
                                value="newsletterOptin"
                                type="checkbox"
                                label="Ik doe mee met de verdubbelaar en ik meld mij aan voor de nieuwsbrief van Lees! Cadeaukaart."
                                ref={register}
                            />
                        </Field>

                        <Button type="submit" disabled={disabled || isLoading}>
                            Doe mee
                        </Button>
                    </form>
                </FormProvider>
            )}
        </FormStyled>
    );
}
